// The notices part: OS notifications for the notices that reach the open
// screen (`notice.show`), the app's badge, and Settings → Notifications.
//
// Promise:
// - A notice becomes an OS notification only as `decide.ts` says; a click
//   opens its screen in this window.
// - The badge is read again on every event that can change what the parts
//   count (an agent's entry or run, a chat read, a turn item changed).
// - The host may add its own panel below the settings (cloud: web push and
//   desktops), and may hide this device's switch when it delivers to devices
//   itself (cloud tells only registered desktops; browsers get web push).
import { type ComponentType, useState } from "react";
import {
  defineScreenPart,
  type ScreenPart,
  type SettingsItem,
} from "../app/screen-part";
import type { RealtimeHandler } from "../realtime/realtime";
import { handOnNotice, NoticesBackground } from "./background";
import { badgeChanged } from "./queries";
import {
  type DesktopNotices,
  noticesSettings,
  OsNoticesQuick,
  shellNotices,
} from "./settings";

export interface NoticesScreenOptions {
  /** The host's controls for its notification destination in the account menu. */
  quickSettings?: ComponentType;
  /** Drawn below this part's settings (cloud: web push and desktops). */
  extraSettings?: ComponentType;
  /** Anchors inside `extraSettings`, found by the settings search. */
  extraSections?: SettingsItem["sections"];
  /**
   * Whether the switch "show OS notifications on this device" is shown
   * (default true). Off where the host decides the destinations itself.
   */
  deviceSwitch?: boolean;
  /**
   * Whether the desktop app may show notifications, where the window bridge
   * that says it is missing (the offline edition's settings window).
   */
  desktopNotices?: DesktopNotices;
  /**
   * Whether the notices this screen's own stream brings become OS
   * notifications (default true). Off where something else shows them (the
   * desktop app: main shows this PC's, the cloud's page in its frame hands
   * over the cloud's).
   */
  osNotices?: boolean;
}

const badge: RealtimeHandler = (_payload, { qc }) => badgeChanged(qc);

// Only an agent's message can be a reply that waits; notices, answers, signals
// and marks leave the count as it was.
const entry: RealtimeHandler = (payload, { qc }) => {
  if ((payload as { kind?: string } | null)?.kind === "message")
    badgeChanged(qc);
};

// Taking back a change to a file (undo) neither archives nor moves a chat.
const chat: RealtimeHandler = (payload, { qc }) => {
  if ((payload as { change?: string } | null)?.change !== "undo")
    badgeChanged(qc);
};

export function createNoticesScreen(
  options: NoticesScreenOptions = {},
): ScreenPart {
  function QuickSettings() {
    const [desktop] = useState(() => options.desktopNotices ?? shellNotices());
    return <OsNoticesQuick desktop={desktop} />;
  }
  return defineScreenPart({
    id: "notices",
    AccountQuickSettings: options.quickSettings ?? QuickSettings,
    Background: NoticesBackground,
    realtime: {
      ...(options.osNotices === false
        ? {}
        : { "notice.show": (payload: unknown) => handOnNotice(payload) }),
      // Archived or deleted: its unread and questions leave the count.
      "agent_chat.changed": chat,
      "agent_chat.entry": entry,
      "agent_chat.run": badge,
      "agent_chat.read": badge,
      "agent_turn.changed": badge,
    },
    settings: noticesSettings({
      deviceSwitch: options.deviceSwitch ?? true,
      desktopNotices: options.desktopNotices,
      Extra: options.extraSettings,
      extraSections: options.extraSections,
    }),
  });
}

/** The part with this device's switch only (the offline edition). */
export const noticesScreen = createNoticesScreen();
